/**
 * The Update panel (#114): one plain form for every tech, opened from the case header ("Update", key `u`) and the
 * phone view. Status, Close (resolution + coverage + time check), Note, Time and Next move go in ONE submit; the API
 * writes each part to Desk under the person's own token and reports each part. A partial failure locks the form and
 * offers "Retry failed parts", which resends the same body with the same idempotency key: the server skips the parts
 * that already succeeded, so nothing is written twice.
 */
import { Button, Checkbox, Dialog, DialogClose, Input, Picker, toast } from '@patchbay/ui'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { AlertTriangle, CheckCircle2, Loader2, RefreshCw, XCircle } from 'lucide-react'
import * as React from 'react'
import type {
  CaseUpdateIn,
  CaseUpdateOut,
  CoverageDecisionValue,
  CoverageDomain,
  FinishCaseIn,
  NextMoveIn,
  NextMoveSuggestion,
  OwnerPerson,
  OwnerSuggestion,
  ResolutionSuggestion,
  StatusSuggestion,
  SuggestionOutcomeIn,
  TimeSuggestion,
  UpdatePartOut,
} from '../../lib/api/generated'
import { invalidateCaseState } from '../../lib/invalidate-case-state'
import { formatOfficeDateTime } from '../../lib/toronto-time'
import { useMe } from '../auth/session'
import { CaseLinksPopover } from '../case-links'
import { owners as listOwners, ownerKeys } from '../case-owner/api'
import { OwnerSelect } from '../case-owner/case-owner'
import { ApiError, caseApi, caseKeys, meDeskQuery } from '../case-view/api'
import { newIdempotencyKey } from '../composer/fingerprint'
import { applyMentions, type PickedMention } from '../composer/mentions'
import { WorkItNowButton } from '../live-work'
import { toLocalInput } from '../next-move/due'
import {
  emptyNextMove,
  humanMessage,
  NextMoveFields,
  NextMoveOwnerPicker,
  type NextMoveValue,
  ownerValueOf,
  toNextMoveIn,
  useContractorFirms,
} from '../next-move/editor'
import { queueKeys } from '../queue/queries'
import { readTechWorkflow, techWorkflowKey } from '../tech-workflow'
import { settlePending, useTrackedDefault } from '../time/tracked'
import { trackedKeys } from '../time/tracked-api'
import { basisForFollow, billingContradictsAgreement, decisionFromBilling, knownAgreement } from './coverage-follow'
import { NoteField } from './note-field'
import { closeTarget, hintForStatus, waitingMoveValue, wcrRepeat } from './pairing'
import type { UpdatePrefill } from './prefill'
import { type ResolutionDraft, ResolutionField } from './resolution-field'
import { SuggestionChips } from './suggestion-chips'
import { effectiveDomain, minutesOf, TIME_DOMAINS, TimeSection, type TimeValue } from './time-section'

export const updateFormKey = (caseUuid: string) => ['case-update-form', caseUuid] as const

const DOMAINS = TIME_DOMAINS
const DOMAIN_LABEL: Record<string, string> = { phone: 'Phone', it: 'IT', macd: 'MACD' }
const DECISIONS: { id: Exclude<CoverageDecisionValue, 'unknown'>; label: string }[] = [
  { id: 'covered', label: 'Covered' },
  { id: 'billable', label: 'Billable' },
  { id: 'partial', label: 'Partly covered' },
]
type CloseReason = 'resolved' | 'duplicate' | 'merged' | 'spam' | 'no_response' | 'other'
const CLOSE_REASONS: { id: CloseReason; label: string }[] = [
  { id: 'resolved', label: 'Resolved' },
  { id: 'duplicate', label: 'Duplicate' },
  { id: 'merged', label: 'Merged' },
  { id: 'spam', label: 'Spam' },
  { id: 'no_response', label: 'No response' },
  { id: 'other', label: 'Other' },
]
const PART_LABEL: Record<string, string> = {
  owner: 'Owner',
  next_move: 'Next move',
  coverage: 'Coverage',
  time: 'Time',
  note: 'Private note',
  resolution: 'Resolution',
  status: 'Status',
  wrapup: 'Wrap-up',
}
type Draft = ResolutionDraft
type SuggestionField = SuggestionOutcomeIn['field']
type FieldSuggestion = NextMoveSuggestion | StatusSuggestion | TimeSuggestion | ResolutionSuggestion | OwnerSuggestion
type PickedSuggestion = { suggestion: FieldSuggestion; snapshot: Record<string, unknown> }
type UpdateSubmission = { kind: 'update'; body: CaseUpdateIn } | { kind: 'finish'; body: FinishCaseIn }

/** #532: a picked "Follow up with X if no reply" keeps its fallback marker while its text is unchanged. */
function withIfNoReply(nm: NextMoveIn | null, picked: PickedSuggestion | undefined, text: string): NextMoveIn | null {
  if (nm?.owner_kind !== 'us' || !picked) return nm
  const value = picked.suggestion.value as { if_no_reply?: boolean; text?: string }
  if (!value.if_no_reply || (value.text ?? '').trim().toLowerCase() !== text.trim().toLowerCase()) return nm
  return { ...nm, if_no_reply: true }
}

/** #1182: same sentence the API returns when Riley cannot draft an alert-only case. */
export function monitoringAlertResolution(subjects: Array<string | null | undefined>): string {
  const parts = subjects.map((raw) => (raw ?? '').replace(/\s+/g, ' ').trim().replace(/\.+$/g, '')).filter(Boolean)
  let subject = parts.length ? parts.join('; ') : 'alert'
  if (subject.length > 500) subject = `${subject.slice(0, 499)}…`
  return `Monitoring alert: ${subject}. Cleared / no action needed.`
}

/** #1110: what Close will do with a monitoring ticket. No customer email, ever. */
function alertCloseCopy(tickets: { ticket_number: string; subject?: string | null }[]): string {
  const ticket = tickets.length === 1 ? tickets[0] : undefined
  if (ticket) {
    // Alert subjects carry long unbreakable ids ("(71aebacc0a29…)"); naming them made the dialog scroll sideways.
    return `Closes alert ticket #${ticket.ticket_number}, no customer email`
  }
  return `Closes ${tickets.length} alert tickets, no customer email.`
}

function Section({ title, children, hint }: { title: string; hint?: React.ReactNode; children: React.ReactNode }) {
  return (
    <section className="min-w-0 max-w-full space-y-2 rounded-lg border border-border p-4">
      <h3 className="flex min-w-0 items-baseline gap-2 text-sm font-semibold">
        {title}
        {hint ? <span className="min-w-0 break-words text-xs font-normal text-muted-foreground">{hint}</span> : null}
      </h3>
      {children}
    </section>
  )
}

function NoteAuthorPicker({
  value,
  onChange,
  people,
  me,
  canDelegate,
}: {
  value: string
  onChange: (value: string) => void
  people?: OwnerPerson[]
  me?: { id: string; name: string; email: string }
  canDelegate: boolean
}) {
  if (!canDelegate || !me) return null
  const staff = people ?? []
  const selfValue = `user:${me.id}`
  const self = staff.find((person) => person.user_id === me.id)
  const options = [
    { value: selfValue, label: `${self?.name ?? me.name} (me)`, detail: self?.email ?? me.email },
    ...staff
      .filter((person) => person.user_id !== me.id)
      .map((person) => ({
        value: person.user_id ? `user:${person.user_id}` : `agent:${person.desk_agent_id}`,
        label: person.name,
        detail: person.user_id ? (person.email ?? '') : 'Desk-only agent',
      })),
  ]
  return (
    <div className="grid gap-1 text-xs font-medium text-muted-foreground">
      Add note as
      <Picker
        aria-label="Add note as"
        value={value || selfValue}
        onChange={(event) => onChange(event.currentTarget.value)}
        className="h-9 w-full rounded-md border border-input bg-card px-3 text-sm text-foreground"
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
            {option.detail ? ` — ${option.detail}` : ''}
          </option>
        ))}
      </Picker>
    </div>
  )
}

function PartRow({ p }: { p: UpdatePartOut }) {
  const good = p.state === 'done' || p.state === 'already_done'
  const amber = p.state === 'pending_token' || p.state === 'skipped'
  const Icon = good ? CheckCircle2 : amber ? AlertTriangle : XCircle
  return (
    <li className="flex items-start gap-2 text-sm" data-part={p.part} data-state={p.state}>
      <Icon className={good ? 'mt-0.5 size-4 text-success' : amber ? 'mt-0.5 size-4 text-warning' : 'mt-0.5 size-4 text-destructive'} />
      <span>
        <span className="font-medium">{PART_LABEL[p.part] ?? p.part}</span>
        <span className="text-muted-foreground"> · {p.state === 'already_done' ? 'already done' : p.state.replace('_', ' ')}</span>
        {p.detail ? <span className="block text-muted-foreground">{p.detail}</span> : null}
      </span>
    </li>
  )
}

export function UpdatePanel({
  open,
  onClose,
  caseUuid,
  caseNumber,
  onUpdated,
  onSubmitSuccess,
  prefill,
}: {
  open: boolean
  onClose: () => void
  caseUuid: string
  caseNumber: string
  /** Extra refresh after a submit (the phone view's own queries). */
  onUpdated?: () => void
  /** Called only when Desk accepted the whole submit (#484: after-send suggestion follow-up). */
  onSubmitSuccess?: () => void
  /** #180: values a suggestion fills in (shown only; written on Submit). Applied each time the panel opens. */
  prefill?: UpdatePrefill | null
}) {
  const qc = useQueryClient()
  const form = useQuery({
    queryKey: updateFormKey(caseUuid),
    queryFn: () => caseApi().updateForm(caseUuid),
    enabled: open && Boolean(caseUuid),
    retry: false,
  })
  const me = useQuery({ ...meDeskQuery(), enabled: open })
  const sessionMe = useMe()
  const workflowGateApplies = sessionMe?.role === 'tech' || sessionMe?.ui_mode === 'simple'
  const workflow = useQuery({
    queryKey: techWorkflowKey(caseUuid),
    queryFn: () => readTechWorkflow(caseUuid),
    enabled: open && Boolean(caseUuid) && workflowGateApplies,
    retry: false,
  })
  // Round 13: the domain pre-pick and each domain's billable default (read-only; unreachable = the API decides).
  const timeDefaults = useQuery({
    queryKey: ['case', caseUuid, 'update-time-defaults'],
    queryFn: () => caseApi().timePrompt(caseUuid),
    enabled: open && Boolean(caseUuid),
    retry: false,
    staleTime: 60_000,
  })
  const firms = useContractorFirms(caseUuid, open)
  // #201: the case owner is the Desk ticket's assignee; the staff list is Desk agents + Patchbay users.
  const staff = useQuery({ queryKey: ownerKeys.people, queryFn: listOwners, enabled: open, staleTime: 60_000, retry: false })
  const f = form.data
  const compactClose = Boolean(prefill?.compactClose)
  const tracked = useTrackedDefault(open && compactClose ? caseUuid : null)

  const [status, setStatus] = React.useState('')
  const [deskTicketId, setDeskTicketId] = React.useState('')
  const [repeatApproved, setRepeatApproved] = React.useState(false)
  const [resolution, setResolution] = React.useState('')
  const [rileyText, setRileyText] = React.useState<string | null>(null)
  const [draft, setDraft] = React.useState<Draft>({ state: 'idle' })
  const [signedOff, setSignedOff] = React.useState(false)
  const [resolutionWarnings, setResolutionWarnings] = React.useState<string[]>([])
  const [coverage, setCoverage] = React.useState<Record<string, { decision: string; basis: string }>>({})
  // #526: the agreement's pre-filled answer. A later edit or the "record as my decision" tick is explicit.
  const coverageSeedRef = React.useRef<Record<string, { decision: string; basis: string }>>({})
  // #1113: a domain the person edited after Billing had filled it. Further billing changes leave that edit alone.
  const coverageEditedRef = React.useRef<Set<string>>(new Set())
  const [coverageConfirmed, setCoverageConfirmed] = React.useState<Record<string, boolean>>({})
  const [coverageExpanded, setCoverageExpanded] = React.useState(false)
  const [note, setNote] = React.useState('')
  const [noteOwner, setNoteOwner] = React.useState('')
  const [mentions, setMentions] = React.useState<PickedMention[]>([])
  const [time, setTime] = React.useState<TimeValue>({ minutes: '', domain: null, billable: null, owner: '' })
  const [noTimeReason, setNoTimeReason] = React.useState('')
  const [closeTimeChoice, setCloseTimeChoice] = React.useState<'time' | 'none'>('none')
  const [closeReason, setCloseReason] = React.useState<CloseReason>('resolved')
  const [notifyCustomer, setNotifyCustomer] = React.useState(true)
  const [ticketNotify, setTicketNotify] = React.useState<Record<string, boolean>>({})
  const [contractorClose, setContractorClose] = React.useState<Record<string, boolean>>({})
  const [signalClose, setSignalClose] = React.useState<Record<string, boolean>>({})
  const [nextMove, setNextMove] = React.useState<NextMoveValue>(() => emptyNextMove())
  const [owner, setOwner] = React.useState('')
  const [wrapup, setWrapup] = React.useState(false)
  const [wrapupOwner, setWrapupOwner] = React.useState('')
  const [wrapupMissing, setWrapupMissing] = React.useState<string[]>(['time', 'notes'])
  const [wrapupExtras, setWrapupExtras] = React.useState(false)
  const [moreFields, setMoreFields] = React.useState(false)
  const [dismissed, setDismissed] = React.useState<Set<string>>(new Set())
  const [picked, setPicked] = React.useState<Partial<Record<SuggestionField, PickedSuggestion>>>({})
  const [busy, setBusy] = React.useState(false)
  const [error, setError] = React.useState<{ message: string; nothingWritten: boolean } | null>(null)
  const [result, setResult] = React.useState<CaseUpdateOut | null>(null)
  const keyRef = React.useRef<string | null>(null)
  const sentRef = React.useRef<UpdateSubmission | null>(null)
  const contractorSeeded = React.useRef(false)
  const signalSeeded = React.useRef(false)
  // Form-derived defaults are applied once per opening: Riley/SSE refetches of the form must never undo what the person chose.
  const formSeeded = React.useRef(false)
  const timeRef = React.useRef<HTMLInputElement>(null)
  const closeTimeControlRef = React.useRef<HTMLDivElement>(null)
  const reportedRef = React.useRef<Set<string>>(new Set())
  const seenRef = React.useRef<Map<string, { field: SuggestionField; suggestion: FieldSuggestion }>>(new Map())
  const compactDefaultsRef = React.useRef(false)
  const alertTemplateRef = React.useRef<string | null>(null)
  const resolutionTouchedRef = React.useRef(false)

  // A fresh form each time it opens.
  // biome-ignore lint/correctness/useExhaustiveDependencies: the prefill is read once per opening
  React.useEffect(() => {
    if (!open) return
    setRepeatApproved(false)
    setDeskTicketId('')
    // #512: never the reply just sent ("thanks, we will close this off"). Riley drafts the resolution once the
    // close is chosen, from the whole case; the reply is not what went wrong, what we did, or how it ended.
    setResolution('')
    setRileyText(null)
    resolutionTouchedRef.current = false
    setDraft({ state: 'idle' })
    setSignedOff(false)
    setResolutionWarnings([])
    setCoverage({})
    setCoverageConfirmed({})
    setCoverageExpanded(false)
    coverageSeedRef.current = {}
    coverageEditedRef.current = new Set()
    setNote('')
    setNoteOwner(me.data?.id ? `user:${me.data.id}` : '')
    setMentions([])
    setTime({ minutes: '', domain: null, billable: null, owner: me.data?.id ? `user:${me.data.id}` : '' })
    setNoTimeReason('')
    setCloseTimeChoice('none')
    setCloseReason('resolved')
    setNotifyCustomer(prefill?.notifyCustomer ?? true)
    setTicketNotify({})
    contractorSeeded.current = false
    signalSeeded.current = false
    formSeeded.current = false
    setContractorClose({})
    setSignalClose({})
    setNextMove(prefill?.nextMove ? { ...prefill.nextMove } : emptyNextMove(me.data?.id))
    setWrapup(false)
    setWrapupOwner(me.data?.id ? `user:${me.data.id}` : '')
    setWrapupMissing(['time', 'notes'])
    setWrapupExtras(false)
    setMoreFields(false)
    setDismissed(new Set())
    setPicked({})
    reportedRef.current = new Set()
    seenRef.current = new Map()
    setError(null)
    setResult(null)
    keyRef.current = null
    sentRef.current = null
    compactDefaultsRef.current = false
  }, [open, me.data?.id])
  // biome-ignore lint/correctness/useExhaustiveDependencies: the prefill is read once per opening
  React.useEffect(() => {
    if (!open || !f || formSeeded.current) return
    formSeeded.current = true
    setOwner(f.owner?.desk_agent_id ?? '')
    setNotifyCustomer(
      prefill?.notifyCustomer ??
        f.close_notification_defaults?.[closeReason] ??
        !['duplicate', 'merged', 'spam', 'no_response'].includes(closeReason),
    )
    setTicketNotify({})
    setDeskTicketId(f.desk_ticket_id ?? '')
    const wanted = prefill?.status?.trim().toLowerCase()
    const match = wanted
      ? f.statuses.find((s) => s.name.toLowerCase() === wanted)?.name
      : compactClose
        ? closeTarget(f.statuses, f.close_targets?.resolved)
        : undefined
    setStatus(match ?? f.current_status ?? '')
  }, [open, f])
  // #513: in the Close dialog the reason picks the Desk status it lands on (server's close_targets, "Closed" today).
  // biome-ignore lint/correctness/useExhaustiveDependencies: re-run only when the reason changes
  React.useEffect(() => {
    if (!open || !f || !compactClose || prefill?.status) return
    const target = closeTarget(f.statuses, f.close_targets?.[closeReason])
    if (target) setStatus(target)
  }, [closeReason])
  // A changed close reason re-applies its notify default; a refetch of the form does not.
  // biome-ignore lint/correctness/useExhaustiveDependencies: re-run only when the reason changes
  React.useEffect(() => {
    if (!open || !f || !formSeeded.current) return
    setNotifyCustomer(
      prefill?.notifyCustomer ??
        f.close_notification_defaults?.[closeReason] ??
        !['duplicate', 'merged', 'spam', 'no_response'].includes(closeReason),
    )
    setTicketNotify({})
  }, [closeReason])
  React.useEffect(() => {
    if (!open || !f || contractorSeeded.current) return
    contractorSeeded.current = true
    const next: Record<string, boolean> = {}
    for (const thread of f.contractor_threads ?? []) next[thread.desk_ticket_id] = thread.close_by_default
    setContractorClose(next)
  }, [open, f])
  React.useEffect(() => {
    if (!open || !f || signalSeeded.current) return
    signalSeeded.current = true
    const next: Record<string, boolean> = {}
    for (const ticket of f.signal_tickets ?? []) next[ticket.desk_ticket_id] = ticket.close_by_default ?? true
    setSignalClose(next)
  }, [open, f])
  React.useEffect(() => {
    if (!open || !compactClose || !f || compactDefaultsRef.current) return
    if (tracked.known && tracked.minutes > 0) {
      compactDefaultsRef.current = true
      setCloseTimeChoice('time')
      setTime((current) => ({ ...current, minutes: String(tracked.minutes) }))
      setNoTimeReason('')
      return
    }
    if (f.last_no_time_reason) {
      compactDefaultsRef.current = true
      setCloseTimeChoice('none')
      setNoTimeReason(f.last_no_time_reason)
    } else if (tracked.known) {
      compactDefaultsRef.current = true
    }
  }, [open, compactClose, f, tracked.known, tracked.minutes])
  const ownerChanged = Boolean(owner) && owner !== (f?.owner?.desk_agent_id ?? '')

  const statuses = f?.statuses ?? []
  const customerTickets = f?.customer_tickets ?? []
  const closeTickets = f?.close_tickets ?? []
  // audience missing means a customer ticket (older forms). notify_offered false without audience stays customer.
  const customerCloseTickets = closeTickets.filter((ticket) => ticket.audience !== 'signal' && ticket.notify_offered !== false)
  const requiredSignalTickets = closeTickets.filter((ticket) => ticket.audience === 'signal')
  const optionalSignalTickets = f?.signal_tickets ?? []
  const checkedSignalTickets = optionalSignalTickets.filter(
    (ticket) => signalClose[ticket.desk_ticket_id] ?? ticket.close_by_default ?? true,
  )
  const selectedTicket = customerTickets.find((ticket) => ticket.desk_ticket_id === deskTicketId)
  const currentStatus = selectedTicket?.status ?? f?.current_status ?? ''
  const typeOf = (name: string) => statuses.find((s) => s.name === name)?.status_type
  const closing = typeOf(status) === 'Closed'
  const changed = Boolean(status) && status !== currentStatus
  // #1157: Close on an open case whose Desk tickets are already Closed still shows the close section.
  // A plain Update dialog does not, so a note can still be saved without a resolution.
  const deskAlreadyClosed = Boolean(compactClose && f?.desk_already_closed)
  const closingAction = closing && (changed || deskAlreadyClosed)
  const statusIntent = status.trim().toLowerCase()
  const waitingCustomer = !closingAction && (statusIntent.includes('waiting client') || statusIntent.includes('waiting customer'))
  const scheduling = !closingAction && (statusIntent.startsWith('scheduling') || statusIntent.startsWith('scheduled'))
  const monitoring = !closingAction && statusIntent.includes('monitor')
  const working = !closingAction && !waitingCustomer && !scheduling
  const suggestionIntent = closingAction ? 'close' : waitingCustomer ? 'waiting' : scheduling ? 'scheduling' : 'working'
  const suggestionQuery = useQuery({
    queryKey: ['case', caseUuid, 'field-suggestions', suggestionIntent],
    queryFn: () => caseApi().suggestions(caseUuid, suggestionIntent),
    enabled: open && Boolean(caseUuid),
    retry: false,
    staleTime: 30_000,
  })
  const repeat = Boolean(f) && wcrRepeat(currentStatus, status)
  const timeMinutes = minutesOf(time)
  const domain = effectiveDomain(time, timeDefaults.data)
  const timeDefault = timeDefaults.data?.domains.find((item) => item.domain === domain)
  const timeBillable = time.billable ?? (timeDefault && timeDefault.decision !== 'unknown' ? timeDefault.billable_default : null)
  const locked = Boolean(result && !result.ok)
  const alertOnly = customerTickets.length === 0 && customerCloseTickets.length === 0 && requiredSignalTickets.length > 0
  const alertTemplateText = alertOnly ? monitoringAlertResolution(requiredSignalTickets.map((ticket) => ticket.subject)) : null
  alertTemplateRef.current = alertTemplateText
  const normResolution = (value: string) => value.replace(/\s+/g, ' ').trim()
  const untouchedRiley = rileyText !== null && normResolution(resolution) === normResolution(rileyText)
  const untouchedAlert = alertTemplateText !== null && normResolution(resolution) === normResolution(alertTemplateText)
  const reviewRequired = untouchedRiley

  const loadDraft = React.useCallback(
    async (redraft: boolean) => {
      setDraft({ state: 'loading' })
      if (redraft) resolutionTouchedRef.current = false
      const keepTyped = () => resolutionTouchedRef.current && !redraft
      const fillAlert = (text: string, detail: string) => {
        if (!text || keepTyped()) return false
        setResolution(text)
        setRileyText(null)
        setSignedOff(false)
        setDraft({ state: 'ready', source: 'alert', detail })
        return true
      }
      try {
        if (!redraft && workflow.data?.resolution_note) {
          setResolution(workflow.data.resolution_note)
          setRileyText(null)
          setSignedOff(false)
          setDraft({ state: 'ready', source: 'workflow', detail: 'Saved in the guided checklist' })
          return
        }
        if (alertTemplateRef.current && !resolutionTouchedRef.current) {
          setResolution((current) => (current.trim() ? current : (alertTemplateRef.current ?? current)))
        }
        const out = await caseApi().resolutionDraft(caseUuid, redraft)
        if (!keepTyped() && out.text && out.source !== 'none' && out.source !== 'alert') {
          setResolution(out.text)
          setRileyText(out.source === 'riley' || out.source === 'summary' ? out.text : null)
          setSignedOff(false) // a new text needs a new review
          setDraft({ state: 'ready', source: out.source, detail: out.basis, stripped: out.stripped ?? [] })
          return
        }
        if (out.source === 'alert' && out.text && fillAlert(out.text, out.basis ?? 'From the monitoring alert')) return
        if (
          (out.source === 'none' || !out.text) &&
          alertTemplateRef.current &&
          fillAlert(alertTemplateRef.current, 'From the monitoring alert')
        ) {
          return
        }
        if (keepTyped()) {
          setDraft({
            state: 'ready',
            source: alertTemplateRef.current ? 'alert' : 'none',
            detail: alertTemplateRef.current ? 'From the monitoring alert' : out.detail,
          })
          return
        }
        setDraft({ state: 'ready', source: 'none', detail: out.detail })
      } catch {
        if (alertTemplateRef.current && fillAlert(alertTemplateRef.current, 'From the monitoring alert')) return
        setDraft({ state: 'ready', source: 'none', detail: 'Riley could not draft a resolution; write it yourself' })
      }
    },
    [caseUuid, workflow.data?.resolution_note],
  )
  React.useEffect(() => {
    if (open && closingAction && draft.state === 'idle' && (!workflowGateApplies || !workflow.isPending)) void loadDraft(false)
  }, [open, closingAction, draft.state, loadDraft, workflowGateApplies, workflow.isPending])
  // #1182: the alert sentence is in the box as soon as Close opens, before Riley answers.
  React.useEffect(() => {
    if (!open || !closingAction || resolutionTouchedRef.current || !alertTemplateText) return
    if (workflow.data?.resolution_note) return
    setResolution((current) => (current.trim() ? current : alertTemplateText))
  }, [open, closingAction, alertTemplateText, workflow.data?.resolution_note])
  React.useEffect(() => {
    if (!open || !closingAction || resolution.trim().length < 3) {
      setResolutionWarnings([])
      return
    }
    let active = true
    const timer = window.setTimeout(() => {
      void caseApi()
        .resolutionCheck(caseUuid, resolution.trim())
        .then((out) => {
          if (active) setResolutionWarnings(out.warnings ?? [])
        })
        .catch(() => {
          if (active) setResolutionWarnings(['guard_unavailable'])
        })
    }, 350)
    return () => {
      active = false
      window.clearTimeout(timer)
    }
  }, [open, closingAction, caseUuid, resolution])

  // Coverage required at close (round 13): the undecided domains, plus the domain of the time logged now.
  const needCoverage: CoverageDomain[] = React.useMemo(() => {
    if (!f || !closingAction) return []
    const out = new Set<CoverageDomain>(f.coverage.undecided)
    const applies = f.coverage.applies || timeMinutes > 0
    if (!applies) return []
    if (timeMinutes > 0) {
      const d = domain ?? 'phone'
      const dec = f.coverage.decisions?.[d]
      if (!dec || dec === 'unknown') out.add(d)
    }
    if (out.size === 0 && !f.coverage.applies && timeMinutes > 0) out.add(domain ?? 'phone')
    return DOMAINS.map((d) => d.id).filter((d) => out.has(d))
  }, [f, closingAction, timeMinutes, domain])

  // #802: when the person chooses Log time, render the prospective domain's one case-coverage control immediately.
  // `needCoverage` still decides what is required/submitted, so a no-time close keeps the existing semantics; this
  // separate display list only prevents typing the first minute from inserting a new block above the active input.
  // #746 will move this UI into the shared time+coverage component used by every entry point.
  const shownCoverage: CoverageDomain[] = React.useMemo(() => {
    if (!f || !closingAction) return []
    const out = new Set<CoverageDomain>(needCoverage)
    if (closeTimeChoice === 'time' || timeMinutes > 0) {
      const d = domain ?? 'phone'
      const stored = f.coverage.decisions?.[d]
      if (!stored || stored === 'unknown') out.add(d)
      // #1113: a billing choice that disagrees with the agreement keeps this domain's full controls on screen.
      if (time.billable !== null && timeMinutes > 0) {
        const line = f.coverageLines.find((item) => item.domain === d)
        const prompt = timeDefaults.data?.domains.find((item) => item.domain === d)
        if (billingContradictsAgreement(time.billable, knownAgreement(line?.decision, prompt?.decision))) out.add(d)
      }
    }
    return DOMAINS.map((item) => item.id).filter((d) => out.has(d))
  }, [f, closingAction, needCoverage, closeTimeChoice, domain, time.billable, timeMinutes, timeDefaults.data])
  React.useEffect(() => {
    if (!f) return
    setCoverage((cur) => {
      const next = { ...cur }
      for (const d of shownCoverage) {
        if (next[d]) continue
        const line = f.coverageLines.find((l) => l.domain === d)
        const known = line && line.decision !== 'unknown' ? line : null
        next[d] = {
          decision: known?.decision ?? '',
          basis: known?.basis || `Decided at close by ${me.data?.name ?? 'me'}`,
        }
      }
      return next
    })
  }, [shownCoverage, f, me.data?.name])

  const shown = suggestionQuery.data
  const nextSuggestions = (shown?.next_move ?? []).filter((s) => !dismissed.has(s.id))
  const statusSuggestions = (shown?.status ?? []).filter((s) => !dismissed.has(s.id))
  const timeSuggestions = (shown?.time ?? []).filter((s) => !dismissed.has(s.id))
  const resolutionSuggestions = (shown?.resolution ?? []).filter((s) => !dismissed.has(s.id))
  const ownerSuggestions = (shown?.owner ?? []).filter((s) => !dismissed.has(s.id))

  // Only visible chips are learning examples. Suggestions inside a collapsed/irrelevant section are not
  // "ignored" merely because the person saved or closed the panel without ever seeing them.
  const visibleSuggestions = React.useMemo<{ field: SuggestionField; suggestion: FieldSuggestion }[]>(() => {
    const visible = (field: SuggestionField) => (shown?.[field] ?? []).filter((suggestion) => !dismissed.has(suggestion.id))
    return [
      ...visible('status').map((suggestion) => ({ field: 'status' as const, suggestion })),
      ...(closingAction ? visible('resolution').map((suggestion) => ({ field: 'resolution' as const, suggestion })) : []),
      ...(!closingAction ? visible('next_move').map((suggestion) => ({ field: 'next_move' as const, suggestion })) : []),
      ...(working || (closingAction && closeTimeChoice === 'time') || (moreFields && !closingAction && !working)
        ? visible('time').map((suggestion) => ({ field: 'time' as const, suggestion }))
        : []),
      ...(moreFields ? visible('owner').map((suggestion) => ({ field: 'owner' as const, suggestion })) : []),
    ]
  }, [shown, dismissed, closingAction, working, closeTimeChoice, moreFields])
  React.useEffect(() => {
    if (!open) return
    for (const row of visibleSuggestions) seenRef.current.set(row.suggestion.id, row)
  }, [open, visibleSuggestions])

  const remember = (field: SuggestionField, suggestion: FieldSuggestion, snapshot: Record<string, unknown>) => {
    setPicked((current) => ({ ...current, [field]: { suggestion, snapshot } }))
  }
  const postLabels = async (
    rows: {
      field: SuggestionField
      suggestion: FieldSuggestion
      outcome: 'accepted' | 'edited' | 'ignored'
      final?: Record<string, unknown>
    }[],
  ) => {
    const fresh = rows.filter((row) => !reportedRef.current.has(row.suggestion.id))
    if (!fresh.length) return
    for (const row of fresh) reportedRef.current.add(row.suggestion.id)
    try {
      await caseApi().suggestionOutcomes(
        caseUuid,
        fresh.map((row) => ({
          suggestion_id: row.suggestion.id,
          field: row.field,
          source: row.suggestion.source,
          outcome: row.outcome,
          original_value: { ...row.suggestion.value },
          ...(row.outcome === 'edited' ? { final_value: row.final ?? {} } : {}),
        })),
      )
    } catch {
      for (const row of fresh) reportedRef.current.delete(row.suggestion.id)
    }
  }
  const ignore = (field: SuggestionField, suggestion: FieldSuggestion) => {
    setDismissed((current) => new Set(current).add(suggestion.id))
    setPicked((current) => (current[field]?.suggestion.id === suggestion.id ? { ...current, [field]: undefined } : current))
    void postLabels([{ field, suggestion, outcome: 'ignored' }])
  }
  const ownerFor = (suggestion: NextMoveSuggestion) => ownerValueOf(suggestion.value) || (me.data?.id ? `user:${me.data.id}` : '')
  const pickNext = (suggestion: NextMoveSuggestion) => {
    const value = {
      text: suggestion.value.text,
      owner: ownerFor(suggestion),
      due: toLocalInput(new Date(suggestion.value.due_at)),
    }
    setNextMove(value)
    remember('next_move', suggestion, value)
  }
  const prefillWaiting = (name: string) => {
    if (compactClose) return
    if (name.trim().toLowerCase() === currentStatus.trim().toLowerCase()) return
    const hint = hintForStatus(f?.waiting_moves, name)
    if (!hint) return
    setNextMove(waitingMoveValue(hint) ?? { text: '', owner: '', due: '' })
  }
  const pickStatus = (suggestion: StatusSuggestion) => {
    setStatus(suggestion.value.name)
    prefillWaiting(suggestion.value.name)
    if (suggestion.value.notify_customer !== null && suggestion.value.notify_customer !== undefined) {
      setNotifyCustomer(suggestion.value.notify_customer)
    }
    remember('status', suggestion, {
      name: suggestion.value.name,
      notify_customer: suggestion.value.notify_customer ?? null,
    })
  }
  const pickTime = (suggestion: TimeSuggestion) => {
    setTime((current) => ({ ...current, minutes: String(suggestion.value.minutes), domain: suggestion.value.domain }))
    remember('time', suggestion, { minutes: suggestion.value.minutes, domain: suggestion.value.domain })
  }
  const pickResolution = (suggestion: ResolutionSuggestion) => {
    resolutionTouchedRef.current = true
    setResolution(suggestion.value.text)
    setRileyText(suggestion.source === 'riley' ? suggestion.value.text : null)
    setSignedOff(false)
    remember('resolution', suggestion, { text: suggestion.value.text.trim() })
  }
  const pickOwner = (suggestion: OwnerSuggestion) => {
    const value = suggestion.value.desk_agent_id ?? ''
    setOwner(value)
    remember('owner', suggestion, { desk_agent_id: value })
  }

  // The after-send card carries values rather than suggestion IDs. Match those values back to this service so the
  // eventual save/cancel is still labelled against the same rule set.
  // biome-ignore lint/correctness/useExhaustiveDependencies: match values once when the server response changes
  React.useEffect(() => {
    if (!open || !prefill) return
    const statusMatch = statusSuggestions.find((s) => s.value.name.toLowerCase() === prefill.status?.toLowerCase())
    const nextMatch = nextSuggestions.find((s) => s.value.text === prefill.nextMove?.text)
    setPicked((current) => ({
      ...current,
      ...(statusMatch && !current.status
        ? {
            status: {
              suggestion: statusMatch,
              snapshot: { name: statusMatch.value.name, notify_customer: statusMatch.value.notify_customer ?? null },
            },
          }
        : {}),
      ...(nextMatch && !current.next_move
        ? {
            next_move: {
              suggestion: nextMatch,
              snapshot: {
                text: prefill.nextMove?.text ?? '',
                owner: prefill.nextMove?.owner ?? '',
                due: prefill.nextMove?.due ?? '',
              },
            },
          }
        : {}),
    }))
  }, [open, prefill, suggestionQuery.data])
  // biome-ignore lint/correctness/useExhaustiveDependencies: the active suggestion list is deliberately captured
  React.useEffect(() => {
    if (!open || !nextSuggestions.length) return
    const onKey = (event: KeyboardEvent) => {
      if (!event.altKey || event.ctrlKey || event.metaKey) return
      const index = Number(event.key) - 1
      const suggestion = nextSuggestions[index]
      if (!suggestion || index < 0 || index > 2) return
      event.preventDefault()
      pickNext(suggestion)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, nextSuggestions])

  const nm = !closingAction ? withIfNoReply(nextMove.text.trim() ? toNextMoveIn(nextMove) : null, picked.next_move, nextMove.text) : null
  const nmIncomplete = !closingAction && Boolean(nextMove.text.trim()) && !nm
  // Capture the pre-filled answer in this render, before `body` is built. An effect would land after the body
  // that a click closes over, and an unchanged prefill would be sent as the person's own decision (#526).
  for (const d of shownCoverage) {
    const row = coverage[d]
    if (!row || coverageSeedRef.current[d]) continue
    coverageSeedRef.current[d] = { decision: row.decision, basis: row.basis }
  }
  const coverageIsExplicit = (d: string): boolean => {
    if (coverageConfirmed[d]) return true
    const seed = coverageSeedRef.current[d]
    const row = coverage[d]
    if (!seed || !row) return Boolean(row?.decision)
    return row.decision !== seed.decision || row.basis.trim() !== seed.basis.trim()
  }
  // #1113: an explicit Billing choice fills this domain unless it disagrees with the agreement or the person edited it.
  const billingTouched = timeMinutes > 0 && time.billable !== null
  const timeDomain: CoverageDomain | null = timeMinutes > 0 ? (domain ?? 'phone') : null
  const agreementOf = (d: CoverageDomain): string | null => {
    const line = f?.coverageLines.find((item) => item.domain === d)
    const prompt = timeDefaults.data?.domains.find((item) => item.domain === d)
    return knownAgreement(line?.decision, prompt?.decision)
  }
  const followedRow = (d: CoverageDomain): { decision: 'covered' | 'billable'; basis: string } | null => {
    if (d !== timeDomain || !billingTouched || time.billable === null || coverageEditedRef.current.has(d)) return null
    const line = f?.coverageLines.find((item) => item.domain === d) ?? null
    const prompt = timeDefaults.data?.domains.find((item) => item.domain === d) ?? null
    if (billingContradictsAgreement(time.billable, knownAgreement(line?.decision, prompt?.decision))) return null
    const decision = decisionFromBilling(time.billable)
    return { decision, basis: basisForFollow(decision, line, prompt) }
  }
  const resolvedCoverage = (d: CoverageDomain): { decision: string; basis: string; explicit: boolean } => {
    const followed = followedRow(d)
    if (followed) return { ...followed, explicit: true }
    const state = coverage[d]
    const line = state ?? f?.coverageLines.find((row) => row.domain === d)
    const explicit = state ? coverageIsExplicit(d) : false
    const storedDecision = f?.coverage.decisions?.[d]
    const keepsStored = !explicit && Boolean(storedDecision) && storedDecision !== 'unknown'
    const decision = (keepsStored ? storedDecision : (line?.decision ?? storedDecision ?? '')) || ''
    const basis = (keepsStored ? (f?.coverage.bases?.[d] ?? '') : (line?.basis ?? f?.coverage.bases?.[d] ?? '')).trim()
    return { decision, basis, explicit }
  }
  const coverageRowIncomplete = (d: CoverageDomain) => {
    const row = resolvedCoverage(d)
    return !row.decision || row.decision === 'unknown' || row.basis.length < 3
  }
  const contradicts = Boolean(
    timeDomain && billingTouched && time.billable !== null && billingContradictsAgreement(time.billable, agreementOf(timeDomain)),
  )
  const involvedDomains: CoverageDomain[] =
    !f || !closingAction
      ? []
      : DOMAINS.map((item) => item.id).filter(
          (d) => needCoverage.includes(d) || (f.coverage.domains_touched ?? []).includes(d) || d === timeDomain,
        )
  const followLine = timeDomain ? followedRow(timeDomain) : null
  const passive = timeDomain && !billingTouched ? resolvedCoverage(timeDomain) : null
  const passiveKnown = Boolean(passive && passive.decision !== 'unknown' && passive.decision !== '' && passive.basis.length >= 3)
  // An edit or a tick on the case-coverage controls keeps those controls open. Billing's own copy does not.
  const personEditedCoverage = Boolean(timeDomain && !followLine && coverageIsExplicit(timeDomain))
  const collapseCoverage = Boolean(
    timeDomain &&
      involvedDomains.length === 1 &&
      !coverageExpanded &&
      !contradicts &&
      !personEditedCoverage &&
      (followLine || passiveKnown),
  )
  const collapseSource = followLine
    ? 'from your time entry'
    : timeDomain && f?.coverage.decisions?.[timeDomain] && f.coverage.decisions[timeDomain] !== 'unknown'
      ? 'already recorded'
      : 'from the agreement'
  // A stored decision already answers the case. A bare agreement prefill does not, so the person has to confirm it.
  const contradictionBlocks = Boolean(
    contradicts &&
      timeDomain &&
      !coverageIsExplicit(timeDomain) &&
      !(f?.coverage.decisions?.[timeDomain] && f.coverage.decisions[timeDomain] !== 'unknown'),
  )
  const billableIncomplete = timeMinutes > 0 && timeBillable === null
  const incompleteCoverageDomains = needCoverage.filter((d) => coverageRowIncomplete(d) && !(collapseCoverage && d === timeDomain))
  const focusCoverage = (d: CoverageDomain) => {
    const row = closeTimeControlRef.current?.querySelector<HTMLElement>(`[data-close-coverage="${d}"]`)
    row?.scrollIntoView?.({ block: 'center' })
    row?.querySelector<HTMLInputElement>(`[aria-label="${DOMAIN_LABEL[d]} coverage"]`)?.focus()
  }
  const focusTimeBilling = () => {
    const input = timeRef.current?.closest('form')?.querySelector<HTMLInputElement>('[aria-label="Billable"]')
    input?.scrollIntoView?.({ block: 'center' })
    input?.focus()
  }
  const notifyForClose = deskAlreadyClosed ? false : notifyCustomer
  const closeNotificationChoices = customerCloseTickets.map((ticket) =>
    deskAlreadyClosed ? false : (ticketNotify[ticket.desk_ticket_id] ?? notifyCustomer),
  )
  const notificationSummary = deskAlreadyClosed
    ? 'No customer email'
    : customerCloseTickets.length === 0
      ? 'No customer email'
      : new Set(closeNotificationChoices).size > 1
        ? 'Customer email varies by ticket'
        : `Customer ${(closeNotificationChoices[0] ?? notifyCustomer) ? 'will' : "won't"} be emailed`
  const sendStatus = changed || (repeat && repeatApproved) || deskAlreadyClosed
  const workingStatusAvailable = statuses.some((item) => item.status_type === 'Open')
  const wrapupOwnerId = wrapupOwner.startsWith('user:') ? wrapupOwner.slice(5) : null
  const wrapupDeskAgentId = wrapupOwner.startsWith('agent:') ? wrapupOwner.slice(6) : null
  const wrapupContractorId = wrapupOwner.startsWith('contractor:') ? wrapupOwner.slice(11) : null
  const body: CaseUpdateIn = {
    idempotency_key: keyRef.current ?? '',
    desk_ticket_id: deskTicketId || f?.desk_ticket_id || null,
    // wrap-up projects an Open-type Desk status itself and is its own action: never send a status or move with it
    status: sendStatus && !wrapup ? status : null,
    confirm_status_repeat: repeat && repeatApproved,
    resolution: closingAction
      ? {
          text: resolution.trim(),
          signed_off: reviewRequired ? signedOff : false,
          source: rileyText !== null ? 'riley' : draft.state === 'ready' && draft.source === 'desk' ? 'desk' : 'person',
          riley_draft: rileyText,
        }
      : null,
    coverage: closingAction
      ? needCoverage.map((d) => {
          const row = resolvedCoverage(d)
          return {
            domain: d,
            decision: row.decision as CoverageDecisionValue,
            basis: row.basis,
            explicit: row.explicit,
          }
        })
      : [],
    note: note.trim()
      ? {
          text: applyMentions(note.trim(), mentions).body,
          ...(noteOwner.startsWith('user:') && noteOwner.slice(5) !== me.data?.id ? { user_id: noteOwner.slice(5) } : {}),
          ...(noteOwner.startsWith('agent:') ? { owner_desk_agent_id: noteOwner.slice(6) } : {}),
        }
      : null,
    time:
      timeMinutes > 0
        ? {
            minutes: timeMinutes,
            domain,
            billable: timeBillable ?? false,
            note: time.note?.trim() || null,
            ...(compactClose && tracked.known ? { tracked_seconds: tracked.seconds } : {}),
            ...(time.owner.startsWith('user:') && time.owner.slice(5) !== me.data?.id ? { user_id: time.owner.slice(5) } : {}),
            ...(time.owner.startsWith('agent:') ? { owner_desk_agent_id: time.owner.slice(6) } : {}),
          }
        : null,
    no_time_reason: closingAction && timeMinutes === 0 && noTimeReason.trim() ? noTimeReason.trim() : null,
    close_notification: closingAction
      ? {
          reason: closeReason,
          notify_customer: notifyForClose,
          tickets: customerCloseTickets
            .filter((ticket) => deskAlreadyClosed || ticketNotify[ticket.desk_ticket_id] !== undefined)
            .map((ticket) => ({
              desk_ticket_id: ticket.desk_ticket_id,
              notify_customer: deskAlreadyClosed ? false : (ticketNotify[ticket.desk_ticket_id] ?? notifyCustomer),
            })),
        }
      : null,
    close_contractor_ticket_ids: closingAction
      ? (f?.contractor_threads ?? []).filter((thread) => contractorClose[thread.desk_ticket_id]).map((thread) => thread.desk_ticket_id)
      : [],
    next_move: closingAction || wrapup ? null : nm,
    owner_desk_agent_id: ownerChanged ? owner : null,
    wrapup: wrapup
      ? {
          initiated_by: 'human',
          owner_kind: wrapupContractorId ? 'contractor' : 'staff',
          owner_user_id: wrapupOwnerId,
          owner_desk_agent_id: wrapupDeskAgentId,
          contractor_id: wrapupContractorId,
          missing: wrapupMissing as Array<'time' | 'notes' | 'service_report' | 'parts'>,
        }
      : null,
  }
  const finishCoverageDomains = closingAction
    ? [...new Set([...(f?.coverage.domains_touched ?? []), ...needCoverage, ...(timeMinutes > 0 ? [domain ?? 'phone'] : [])])]
    : []
  const finishCoverage = finishCoverageDomains.map((d) => {
    const row = resolvedCoverage(d)
    return {
      domain: d,
      decision: row.decision as CoverageDecisionValue,
      basis: row.basis,
      explicit: row.explicit,
    }
  })
  const finishCoverageIncomplete = finishCoverage.some((row) => !row.decision || row.decision === 'unknown' || row.basis.length < 3)
  const finishTimeChoice: FinishCaseIn['time_choice'] = timeMinutes > 0 ? 'log' : noTimeReason.trim() ? 'no_time' : 'existing'
  const billingDecision: FinishCaseIn['billing_decision'] =
    finishTimeChoice === 'log' ? (timeBillable ? 'billable' : 'not_billable') : finishTimeChoice === 'no_time' ? 'no_time' : 'existing'
  const finishPreviewTime = finishTimeChoice === 'log' ? `${timeMinutes} min` : finishTimeChoice === 'no_time' ? 'no time' : 'existing time'
  const finishPreviewNotification =
    customerCloseTickets.length === 0
      ? 'No customer email'
      : new Set(closeNotificationChoices).size > 1
        ? 'mixed email choices'
        : (closeNotificationChoices[0] ?? notifyCustomer)
          ? 'email'
          : 'silent'
  const customerTicketIds = new Set(customerTickets.map((ticket) => ticket.desk_ticket_id))
  const signalAnchor =
    [...requiredSignalTickets, ...checkedSignalTickets].find((ticket) => ticket.desk_ticket_id === deskTicketId)?.desk_ticket_id ??
    requiredSignalTickets[0]?.desk_ticket_id ??
    checkedSignalTickets[0]?.desk_ticket_id ??
    ''
  const finishDeskTicketId = customerTicketIds.has(deskTicketId) ? deskTicketId : (customerTickets[0]?.desk_ticket_id ?? signalAnchor)
  const finishBody: FinishCaseIn | null =
    closingAction && f && body.resolution
      ? {
          idempotency_key: keyRef.current ?? '',
          initiated_by: 'human',
          desk_ticket_id: finishDeskTicketId,
          resolution: body.resolution,
          time_choice: finishTimeChoice,
          time: finishTimeChoice === 'log' ? body.time : null,
          no_time_reason: finishTimeChoice === 'no_time' ? noTimeReason.trim() : null,
          coverage: finishCoverage,
          billing_decision: billingDecision,
          next_moves: (f.close_next_moves ?? []).map((move) => ({ id: move.id, text: move.text, state: move.state })),
          status,
          close_reason: closeReason,
          tickets: [
            ...customerCloseTickets.map((ticket) => ({
              desk_ticket_id: ticket.desk_ticket_id,
              ticket_number: ticket.ticket_number,
              current_status: ticket.status,
              notify_customer: deskAlreadyClosed ? false : (ticketNotify[ticket.desk_ticket_id] ?? notifyCustomer),
              audience: 'customer' as const,
            })),
            ...requiredSignalTickets.map((ticket) => ({
              desk_ticket_id: ticket.desk_ticket_id,
              ticket_number: ticket.ticket_number,
              current_status: ticket.status,
              notify_customer: false,
              audience: 'signal' as const,
            })),
            ...checkedSignalTickets.map((ticket) => ({
              desk_ticket_id: ticket.desk_ticket_id,
              ticket_number: ticket.ticket_number,
              current_status: ticket.status,
              notify_customer: false,
              audience: 'signal' as const,
            })),
          ],
          close_contractor_ticket_ids: body.close_contractor_ticket_ids,
          note: body.note,
          owner_desk_agent_id: body.owner_desk_agent_id,
        }
      : null
  const nothing =
    !body.status && !body.note && !body.time && !body.next_move && !body.no_time_reason && !body.owner_desk_agent_id && !body.wrapup
  const wrapupIncomplete = wrapup && ((!wrapupOwnerId && !wrapupDeskAgentId && !wrapupContractorId) || wrapupMissing.length === 0)
  // §5.3 (#271): a close from Patchbay needs the time spent, or "No time" with a reason, in this same submit (unless
  // the case already has time since it last reopened). Enforced here and by the API, never left to a blocker.
  const timeIncomplete = closingAction && timeMinutes === 0 && !f?.close_time_satisfied && noTimeReason.trim().length < 3
  const coverageIncomplete = needCoverage.some((d) => coverageRowIncomplete(d) && !(collapseCoverage && d === timeDomain))
  const closeIncomplete =
    closingAction &&
    (resolution.trim().length < 3 ||
      (reviewRequired && !signedOff) ||
      coverageIncomplete ||
      finishCoverageIncomplete ||
      contradictionBlocks ||
      timeIncomplete ||
      closeTickets.length === 0 ||
      !finishBody?.desk_ticket_id)
  const remainingWorkflowGates = (workflow.data?.gates ?? []).filter(
    (gate) =>
      !gate.satisfied &&
      !(gate.key === 'time' && (timeMinutes > 0 || noTimeReason.trim().length >= 3)) &&
      !(gate.key === 'resolution' && resolution.trim().length >= 3),
  )
  const workflowIncomplete =
    closingAction && workflowGateApplies && (workflow.isPending || !workflow.data || remainingWorkflowGates.length > 0)
  const blockedRole = me.data?.role === 'billing'
  const canSave =
    !busy &&
    !locked &&
    !nothing &&
    !closeIncomplete &&
    !workflowIncomplete &&
    !wrapupIncomplete &&
    !nmIncomplete &&
    !billableIncomplete &&
    !blockedRole &&
    Boolean(f)

  // #1182: every reason canSave is false is named next to the button, for Close and for a plain Update.
  const saveBlockedReasons: string[] = []
  if (f && !busy && !locked && !canSave) {
    if (blockedRole) {
      saveBlockedReasons.push('The billing role writes nothing to Desk')
    } else if (closingAction) {
      if (resolution.trim().length < 3) saveBlockedReasons.push('Write a resolution')
      if (reviewRequired && !signedOff) saveBlockedReasons.push('Review the resolution')
      if (timeIncomplete) saveBlockedReasons.push('Choose time or No time')
      if (billableIncomplete) saveBlockedReasons.push('Choose whether this time is billed')
      const missingCoverage = new Set<string>([
        ...incompleteCoverageDomains,
        ...finishCoverage.filter((row) => !row.decision || row.decision === 'unknown' || row.basis.length < 3).map((row) => row.domain),
      ])
      for (const item of DOMAINS) {
        if (missingCoverage.has(item.id)) saveBlockedReasons.push(`Choose ${DOMAIN_LABEL[item.id]} case coverage`)
      }
      if (contradictionBlocks && timeDomain && !missingCoverage.has(timeDomain)) {
        saveBlockedReasons.push(`Set ${DOMAIN_LABEL[timeDomain]} case coverage`)
      }
      if (closeTickets.length === 0 || !finishBody?.desk_ticket_id) saveBlockedReasons.push('Choose a ticket to close')
      if (workflowGateApplies && workflow.isPending) saveBlockedReasons.push('Complete the guided workflow')
      else if (workflowGateApplies && !workflow.data) saveBlockedReasons.push('Choose and complete a guided workflow')
      else if (workflowGateApplies) {
        for (const gate of remainingWorkflowGates) saveBlockedReasons.push(gate.label)
      }
    } else {
      if (wrapupIncomplete) saveBlockedReasons.push('Pick a technician and at least one missing item')
      if (billableIncomplete) saveBlockedReasons.push('Choose whether this time is billed')
      if (nmIncomplete) saveBlockedReasons.push('Pick an owner and a due time, or clear the text.')
      if (saveBlockedReasons.length === 0) {
        saveBlockedReasons.push(repeat && !repeatApproved ? 'Confirm setting this status again' : 'Nothing to save yet')
      }
    }
  }

  const currentSnapshot = (field: SuggestionField, accepted: Record<string, unknown>): Record<string, unknown> => {
    if (field === 'next_move') return { text: nextMove.text, owner: nextMove.owner, due: nextMove.due }
    if (field === 'status') {
      return {
        name: status,
        notify_customer: accepted.notify_customer === null ? null : notifyCustomer,
      }
    }
    if (field === 'time') return { minutes: timeMinutes, domain }
    if (field === 'resolution') return { text: resolution.trim() }
    return { desk_agent_id: owner }
  }
  const labelAfterSave = async () => {
    const selected = Object.entries(picked).flatMap(([field, choice]) => {
      if (!choice) return []
      const final = currentSnapshot(field as SuggestionField, choice.snapshot)
      const accepted = JSON.stringify(final) === JSON.stringify(choice.snapshot)
      return [
        {
          field: field as SuggestionField,
          suggestion: choice.suggestion,
          outcome: accepted ? ('accepted' as const) : ('edited' as const),
          ...(accepted ? {} : { final }),
        },
      ]
    })
    const selectedIds = new Set(selected.map((row) => row.suggestion.id))
    const ignored = [...seenRef.current.values()]
      .filter((row) => !selectedIds.has(row.suggestion.id))
      .map((row) => ({ ...row, outcome: 'ignored' as const }))
    await postLabels([...selected, ...ignored])
  }
  const cancel = () => {
    const choices = Object.entries(picked).flatMap(([field, choice]) =>
      choice ? [{ field: field as SuggestionField, suggestion: choice.suggestion }] : [],
    )
    const byId = new Map([...seenRef.current.values(), ...choices].map((row) => [row.suggestion.id, row]))
    void postLabels([...byId.values()].map((row) => ({ ...row, outcome: 'ignored' as const })))
    onClose()
  }

  const submit = async (submission: UpdateSubmission) => {
    setBusy(true)
    setError(null)
    try {
      const out =
        submission.kind === 'finish'
          ? await caseApi().finishCase(caseUuid, submission.body)
          : await caseApi().submitUpdate(caseUuid, submission.body)
      setResult(out)
      await Promise.all([
        qc.invalidateQueries({ queryKey: caseKeys.case(caseNumber) }),
        qc.invalidateQueries({ queryKey: queueKeys.all }),
        qc.invalidateQueries({ queryKey: updateFormKey(caseUuid) }),
        qc.invalidateQueries({ queryKey: ownerKeys.case(caseUuid) }),
        // #226: everything keyed by the case uuid too (flags, next moves, threads on other screens).
        qc.invalidateQueries({ queryKey: ['case', caseUuid] }),
        qc.invalidateQueries({ queryKey: caseKeys.section(caseNumber, 'activity') }),
      ])
      void invalidateCaseState(qc, [caseNumber, caseUuid]) // #271: close-out chips and aggregates (background)
      onUpdated?.()
      if (out.ok) {
        await labelAfterSave()
        const submittedTime = submission.body.time
        if (submittedTime?.tracked_seconds) {
          settlePending(caseUuid)
          void qc.invalidateQueries({ queryKey: trackedKeys.case(caseUuid) })
          void qc.invalidateQueries({ queryKey: trackedKeys.unlogged })
        }
        keyRef.current = null
        sentRef.current = null
        const wrapupPart = out.parts.find((part) => part.part === 'wrapup')
        const wrapupHint = wrapupPart?.data && typeof wrapupPart.data.hint === 'string' ? wrapupPart.data.hint.trim() : ''
        toast(
          closingAction
            ? deskAlreadyClosed
              ? `${caseNumber} closed in Patchbay.`
              : `${caseNumber} closed in Desk.`
            : wrapup
              ? `${caseNumber} moved to Wrap-up.${wrapupHint ? ` ${wrapupHint}` : ''}`
              : `${caseNumber} updated.`,
        )
        onSubmitSuccess?.()
        onClose()
      }
    } catch (e) {
      // Keep the key after transport/stale failures: a committed time/note may need reconciliation on retry.
      // Only a pre-write validation refusal, or a key collision, can safely start a fresh attempt.
      const nothingWritten = e instanceof ApiError && (e.code === 'idempotency_key_reused' || e.status === 422)
      setError({ message: humanMessage(e), nothingWritten })
      if (nothingWritten) {
        keyRef.current = null
        sentRef.current = null
      }
    } finally {
      setBusy(false)
    }
  }
  const save = () => {
    if (!canSave) return
    keyRef.current ??= newIdempotencyKey()
    const submission: UpdateSubmission =
      closingAction && finishBody
        ? { kind: 'finish', body: { ...finishBody, idempotency_key: keyRef.current } }
        : { kind: 'update', body: { ...body, idempotency_key: keyRef.current } }
    sentRef.current = submission
    void submit(submission)
  }
  const retry = () => sentRef.current && void submit(sentRef.current)
  const retryable = result?.parts.some((p) => p.retryable && p.state !== 'done' && p.state !== 'already_done')

  const token = me.data?.desk_token
  // #1233: what the person typed (resolution, notes, time). Riley's draft, the alert template and the tracked-time
  // pre-fill are not theirs, so they never make the panel dirty; a submit that went through is not dirty either.
  const timeSeed = compactClose && tracked.known && tracked.minutes > 0 ? String(tracked.minutes) : ''
  const dirty =
    !result &&
    ((resolutionTouchedRef.current && resolution.trim() !== '') ||
      note.trim() !== '' ||
      (time.minutes.trim() !== '' && time.minutes !== timeSeed))
  return (
    <Dialog
      open={open}
      onOpenChange={(o) => !o && cancel()}
      dirty={dirty}
      title={`${compactClose ? 'Close' : 'Update'} ${caseNumber}`}
      description={
        closingAction
          ? deskAlreadyClosed
            ? 'Desk is already closed; this closes the case in Patchbay'
            : customerCloseTickets.length === 0
              ? alertCloseCopy(requiredSignalTickets)
              : `Finishes ${customerCloseTickets.length} customer ticket${customerCloseTickets.length === 1 ? '' : 's'} as you.`
          : selectedTicket
            ? `Writes to Desk #${selectedTicket.ticket_number} as you.`
            : 'Writes to Desk as you.'
      }
      className={closingAction ? 'p-4 [&_[data-slot=dialog-header]]:mb-2' : undefined}
    >
      {form.isPending ? (
        <p className="text-sm text-muted-foreground">
          <Loader2 className="mr-1 inline size-4 animate-spin" /> Loading…
        </p>
      ) : form.isError || !f ? (
        <p role="alert" className="text-sm text-destructive">
          Could not load the update form: {humanMessage(form.error)}
        </p>
      ) : (
        <form
          className={closingAction ? 'min-w-0 max-w-full space-y-2' : 'min-w-0 max-w-full space-y-3'}
          onSubmit={(e) => {
            e.preventDefault()
            save()
          }}
        >
          {blockedRole ? <p className="text-sm text-muted-foreground">The billing role writes nothing to Desk.</p> : null}
          {compactClose && tracked.error ? (
            <p role="status" className="rounded bg-warning-soft px-2 py-1.5 text-sm text-warning">
              Tracked time is unreachable: {tracked.error} Enter the time yourself; zero has not been assumed.
            </p>
          ) : null}
          {token && token !== 'connected' && !blockedRole ? (
            <p className="rounded bg-warning-soft px-2 py-1.5 text-sm text-warning">
              Your Desk login is not connected, so Desk parts will not be written.{' '}
              <Link to="/me" className="font-medium underline">
                Connect Desk
              </Link>
            </p>
          ) : null}
          <fieldset disabled={locked || busy} className={closingAction ? 'min-w-0 max-w-full space-y-2' : 'min-w-0 max-w-full space-y-3'}>
            {!compactClose ? (
              <Section title="Status" hint={currentStatus ? `Now: ${currentStatus}` : undefined}>
                <div className="flex flex-wrap items-center gap-2 [&_button]:h-9">
                  {customerTickets.length > 1 ? (
                    <Picker
                      aria-label="Customer ticket for this update"
                      className="h-9 min-w-40 flex-1 rounded-md border border-input bg-card px-2 text-sm"
                      value={deskTicketId}
                      onValueChange={(next) => {
                        setDeskTicketId(next)
                        setStatus(customerTickets.find((ticket) => ticket.desk_ticket_id === next)?.status ?? '')
                        setRepeatApproved(false)
                      }}
                    >
                      {customerTickets.map((ticket) => (
                        <option key={ticket.desk_ticket_id} value={ticket.desk_ticket_id}>
                          #{ticket.ticket_number}
                          {ticket.status ? ` · ${ticket.status}` : ''}
                        </option>
                      ))}
                    </Picker>
                  ) : null}
                  <Picker
                    aria-label="Desk status"
                    className="h-9 min-w-56 flex-1 rounded-md border border-input bg-card px-2 text-sm"
                    value={status}
                    onValueChange={(next) => {
                      setStatus(next)
                      setRepeatApproved(false)
                      prefillWaiting(next)
                    }}
                    options={[
                      ...(!currentStatus ? [{ value: '', label: 'Pick a status' }] : []),
                      ...['Open', 'On Hold', 'Closed'].flatMap((group) =>
                        statuses
                          .filter((s) => s.status_type === group)
                          .map((s) => ({
                            value: s.name,
                            label: s.name,
                            group,
                            description: group === 'Closed' ? 'Closes the ticket' : undefined,
                            current: s.name === currentStatus,
                          })),
                      ),
                      ...statuses
                        .filter((s) => !['Open', 'On Hold', 'Closed'].includes(s.status_type))
                        .map((s) => ({ value: s.name, label: s.name, group: s.status_type || 'Other', current: s.name === currentStatus })),
                    ]}
                  />
                  {working && caseUuid ? <WorkItNowButton caseId={caseUuid} size="sm" /> : null}
                  <Button
                    type="button"
                    className="h-9"
                    variant={wrapup ? 'primary' : 'outline'}
                    onClick={() =>
                      setWrapup((value) => {
                        if (!value) {
                          setWrapupExtras(false)
                          const linked = f?.wrapup_default_owner
                          const assigned = linked?.owner_user_id
                            ? `user:${linked.owner_user_id}`
                            : linked?.owner_desk_agent_id
                              ? `agent:${linked.owner_desk_agent_id}`
                              : linked?.contractor_id
                                ? `contractor:${linked.contractor_id}`
                                : ownerValueOf(f?.next_move)
                          if (assigned.startsWith('user:') || assigned.startsWith('agent:') || assigned.startsWith('contractor:')) {
                            setWrapupOwner(assigned)
                          }
                          setStatus(currentStatus)
                          setRepeatApproved(false)
                        }
                        return !value
                      })
                    }
                  >
                    Work done – waiting on tech
                  </Button>
                </div>
                <SuggestionChips
                  suggestions={statusSuggestions}
                  label={(suggestion) => suggestion.value.name}
                  selectedId={picked.status?.suggestion.id}
                  onPick={pickStatus}
                  onIgnore={(suggestion) => ignore('status', suggestion)}
                />
                {wrapup ? (
                  <div className="rounded-md border border-border bg-muted/30 p-4">
                    <fieldset className="space-y-3">
                      <legend className="sr-only">Work done – waiting on tech details</legend>
                      <p className="text-sm text-muted-foreground">
                        Move this case to Wrap-up and chase the technician until the selected paperwork arrives.
                      </p>
                      <div className="grid gap-1 text-sm font-medium">
                        <p>Who owes the paperwork</p>
                        <NextMoveOwnerPicker
                          scope="people"
                          ariaLabel="Technician who owes the paperwork"
                          className="h-9"
                          value={wrapupOwner}
                          onChange={setWrapupOwner}
                          people={f.people ?? []}
                          staff={f.staff}
                          contractors={firms.isError ? null : firms.data}
                        />
                      </div>
                      <fieldset className="space-y-1">
                        <legend className="text-sm font-medium">Missing paperwork</legend>
                        <div className="grid gap-1 sm:grid-cols-2">
                          {(
                            [
                              { id: 'time', label: 'Time' },
                              { id: 'notes', label: 'Tech notes' },
                              { id: 'service_report', label: 'Service report' },
                              { id: 'parts', label: 'Parts used' },
                            ] as const
                          ).map(({ id, label }) => (
                            <label key={id} className="flex items-center gap-2 text-sm">
                              <Checkbox
                                checked={wrapupMissing.includes(id)}
                                onCheckedChange={(checked) =>
                                  setWrapupMissing((current) =>
                                    checked ? [...new Set([...current, id])] : current.filter((item) => item !== id),
                                  )
                                }
                                aria-label={`${label} missing`}
                              />
                              {label}
                            </label>
                          ))}
                        </div>
                      </fieldset>
                      {!workingStatusAvailable ? (
                        <p className="text-xs text-warning">
                          Desk status unchanged: no Open-type status is available. Patchbay will still start wrap-up.
                        </p>
                      ) : (
                        <p className="text-xs text-muted-foreground">Desk will be set to an Open working status when you save.</p>
                      )}
                    </fieldset>
                  </div>
                ) : null}
                {repeat ? (
                  <div role="alert" className="space-y-1 rounded bg-warning-soft px-2 py-1.5 text-sm text-warning">
                    <p>Already Waiting Client Response. Desk emails the customer every time this is set.</p>
                    <label className="flex items-center gap-2 text-foreground">
                      <Checkbox
                        checked={repeatApproved}
                        onCheckedChange={(v) => setRepeatApproved(Boolean(v))}
                        aria-label="Set it again anyway"
                      />
                      Set it again anyway (the customer gets another email)
                    </label>
                  </div>
                ) : null}
              </Section>
            ) : null}

            {closingAction ? (
              <Section
                title="Close"
                hint={
                  customerCloseTickets.length === 0
                    ? alertCloseCopy(requiredSignalTickets)
                    : (f.contractor_threads?.length ?? 0) > 0
                      ? `${customerCloseTickets.length} customer ticket${customerCloseTickets.length === 1 ? '' : 's'}`
                      : `${customerCloseTickets.length} customer ticket${customerCloseTickets.length === 1 ? '' : 's'} · vendor/internal stay open`
                }
              >
                {deskAlreadyClosed ? (
                  <p className="text-sm text-muted-foreground">Desk is already closed; this closes the case in Patchbay</p>
                ) : null}
                <div className="flex items-center gap-2 text-sm font-medium">
                  <span>Reason</span>
                  <Picker
                    ariaLabel="Close reason"
                    className="h-8 flex-1 rounded-md border border-input bg-card px-2 text-sm"
                    value={closeReason}
                    onValueChange={(value) => setCloseReason(value as CloseReason)}
                    options={CLOSE_REASONS.map((reason) => ({ value: reason.id, label: reason.label }))}
                  />
                </div>
                <ResolutionField
                  value={resolution}
                  onChange={(text) => {
                    resolutionTouchedRef.current = true
                    setResolution(text)
                    setSignedOff(false)
                  }}
                  draft={draft}
                  warnings={resolutionWarnings}
                  reviewRequired={reviewRequired}
                  untouchedRiley={untouchedRiley}
                  untouchedAlert={untouchedAlert}
                  signedOff={signedOff}
                  onSignedOff={setSignedOff}
                  onRegenerate={() => void loadDraft(true)}
                >
                  <SuggestionChips
                    suggestions={resolutionSuggestions}
                    label={(suggestion) => suggestion.value.text}
                    selectedId={picked.resolution?.suggestion.id}
                    onPick={pickResolution}
                    onIgnore={(suggestion) => ignore('resolution', suggestion)}
                  />
                </ResolutionField>
                <div
                  ref={closeTimeControlRef}
                  className="space-y-2 rounded-md bg-muted px-2.5 py-2 text-sm"
                  data-testid="close-time-control"
                >
                  {f.close_time_satisfied && timeMinutes === 0 && !noTimeReason ? (
                    <div className="flex items-center justify-between gap-2">
                      <span>Time requirement already met.</span>
                      <Button type="button" size="sm" variant="ghost" onClick={() => setCloseTimeChoice('time')}>
                        Add time
                      </Button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <span className="font-medium">Time</span>
                      <Button
                        type="button"
                        size="sm"
                        variant={closeTimeChoice === 'time' ? 'primary' : 'outline'}
                        aria-pressed={closeTimeChoice === 'time'}
                        onClick={() => {
                          setCloseTimeChoice('time')
                          setNoTimeReason('')
                          requestAnimationFrame(() => timeRef.current?.focus())
                        }}
                      >
                        Log time
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant={closeTimeChoice === 'none' ? 'primary' : 'outline'}
                        aria-pressed={closeTimeChoice === 'none'}
                        onClick={() => {
                          setCloseTimeChoice('none')
                          setTime((current) => ({ ...current, minutes: '' }))
                        }}
                      >
                        No time
                      </Button>
                    </div>
                  )}
                  {closeTimeChoice === 'time' ? (
                    <div className="space-y-2">
                      <TimeSection
                        value={time}
                        onChange={setTime}
                        inputRef={timeRef}
                        defaults={timeDefaults.data}
                        people={f.staff}
                        meId={me.data?.id}
                        canDelegate={me.data?.role === 'manager' || me.data?.role === 'cover'}
                      />
                      <SuggestionChips
                        suggestions={timeSuggestions}
                        label={(suggestion) => `${suggestion.value.minutes} min · ${DOMAIN_LABEL[suggestion.value.domain]}`}
                        selectedId={picked.time?.suggestion.id}
                        onPick={pickTime}
                        onIgnore={(suggestion) => ignore('time', suggestion)}
                      />
                    </div>
                  ) : !f.close_time_satisfied || noTimeReason ? (
                    <Input
                      aria-label="No time reason"
                      placeholder="Why no time is being logged"
                      value={noTimeReason}
                      onChange={(event) => setNoTimeReason(event.currentTarget.value)}
                    />
                  ) : null}
                  {collapseCoverage && timeDomain ? (
                    <div className="border-t border-border pt-2" data-testid="close-coverage-controls">
                      <p data-testid="case-coverage-follow" className="text-sm">
                        <span className="font-medium">Case coverage:</span> {DOMAIN_LABEL[timeDomain]} ·{' '}
                        {DECISIONS.find((item) => item.id === resolvedCoverage(timeDomain).decision)?.label ??
                          resolvedCoverage(timeDomain).decision}{' '}
                        ({collapseSource}) ·{' '}
                        <button
                          type="button"
                          className="underline"
                          aria-label={`Change ${DOMAIN_LABEL[timeDomain]} case coverage`}
                          onClick={() => setCoverageExpanded(true)}
                        >
                          change
                        </button>
                      </p>
                    </div>
                  ) : shownCoverage.length ? (
                    <div className="space-y-2 border-t border-border pt-2" data-testid="close-coverage-controls">
                      <p className="text-sm font-medium">Case coverage</p>
                      {contradicts ? (
                        <p className="text-xs text-warning" data-testid="case-coverage-contradiction">
                          Your billing choice doesn't match the agreement
                          {timeDomain && agreementOf(timeDomain)
                            ? ` (${DOMAIN_LABEL[timeDomain]} · ${
                                DECISIONS.find((item) => item.id === agreementOf(timeDomain))?.label ?? agreementOf(timeDomain)
                              })`
                            : ''}
                          . Set the case coverage.
                        </p>
                      ) : (
                        <p className="text-xs text-muted-foreground">
                          A pre-filled answer stays the agreement's until you change it or record it as your decision.
                        </p>
                      )}
                      {shownCoverage.map((d) => {
                        const row = resolvedCoverage(d)
                        return (
                          <div key={d} data-close-coverage={d} className="grid gap-1 sm:grid-cols-[6rem_10rem_1fr] sm:items-center">
                            <span className="text-sm">{DOMAIN_LABEL[d]}</span>
                            <Picker
                              aria-label={`${DOMAIN_LABEL[d]} coverage`}
                              className="h-8 rounded-md border border-input bg-card px-2 text-sm"
                              value={row.decision}
                              onChange={(e) => {
                                const decision = e.currentTarget.value
                                coverageEditedRef.current.add(d)
                                setCoverage((c) => ({ ...c, [d]: { decision, basis: row.basis } }))
                              }}
                            >
                              <option value="">Decide…</option>
                              {DECISIONS.map((x) => (
                                <option key={x.id} value={x.id}>
                                  {x.label}
                                </option>
                              ))}
                            </Picker>
                            <Input
                              aria-label={`${DOMAIN_LABEL[d]} coverage reason`}
                              value={row.basis}
                              onChange={(e) => {
                                const basis = e.currentTarget.value
                                coverageEditedRef.current.add(d)
                                setCoverage((c) => ({ ...c, [d]: { decision: row.decision, basis } }))
                              }}
                            />
                            <label className="flex items-center gap-2 text-xs text-muted-foreground sm:col-span-3">
                              <Checkbox
                                checked={Boolean(coverageConfirmed[d]) || Boolean(followedRow(d))}
                                onCheckedChange={(v) => {
                                  coverageEditedRef.current.add(d)
                                  setCoverage((c) => ({ ...c, [d]: { decision: row.decision, basis: row.basis } }))
                                  setCoverageConfirmed((c) => ({ ...c, [d]: Boolean(v) }))
                                }}
                                aria-label={`Record ${DOMAIN_LABEL[d]} coverage as my decision`}
                              />
                              Record as my decision
                            </label>
                          </div>
                        )
                      })}
                    </div>
                  ) : null}
                </div>
                <div className="space-y-2 rounded-md border border-border px-2.5 py-2">
                  {customerCloseTickets.length > 0 && f.emailed_customer_today && !deskAlreadyClosed ? (
                    <p className="text-xs text-muted-foreground" data-testid="emailed-today-note">
                      We already emailed the customer today, so Desk's closing email starts off. Tick Notify customer to send it anyway.
                    </p>
                  ) : null}
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                    {deskAlreadyClosed ? (
                      <p className="text-sm text-muted-foreground">No customer email</p>
                    ) : customerCloseTickets.length > 0 ? (
                      <label className="flex flex-wrap items-center gap-2 text-sm font-medium">
                        <Checkbox
                          checked={notifyCustomer}
                          onCheckedChange={(value) => setNotifyCustomer(Boolean(value))}
                          aria-label="Notify customer"
                        />
                        Notify customer
                        <span className="font-normal text-muted-foreground">{notificationSummary}</span>
                      </label>
                    ) : (
                      <p className="text-sm text-muted-foreground">{alertCloseCopy(requiredSignalTickets)}</p>
                    )}
                    <details className="group min-w-0 basis-48 flex-1 text-xs" data-testid="finish-case-preview">
                      <summary className="flex cursor-pointer list-none items-center gap-2 [&::-webkit-details-marker]:hidden">
                        <span className="shrink-0 font-medium">Finish preview</span>
                        <span className="min-w-0 flex-1 truncate text-muted-foreground">
                          {customerCloseTickets.length === 0
                            ? `${status || 'Closed status required'} · ${alertCloseCopy(requiredSignalTickets)} · ${finishPreviewTime}`
                            : `${status || 'Closed status required'} · ${customerCloseTickets.length} customer ticket${
                                customerCloseTickets.length === 1 ? '' : 's'
                              } · ${finishPreviewTime} · ${finishPreviewNotification}`}
                        </span>
                        <span className="shrink-0 text-muted-foreground group-open:hidden">Show</span>
                        <span className="hidden shrink-0 text-muted-foreground group-open:inline">Hide</span>
                      </summary>
                      <div className="mt-2 space-y-1.5 border-t border-border pt-2">
                        <p>
                          <span className="font-medium">Resolution:</span>{' '}
                          <span className="whitespace-pre-wrap">{resolution.trim() || 'Required'}</span>
                        </p>
                        <p>
                          <span className="font-medium">Time:</span>{' '}
                          {finishTimeChoice === 'log'
                            ? `${timeMinutes} min · ${billingDecision === 'billable' ? 'billable' : 'not billable'}`
                            : finishTimeChoice === 'no_time'
                              ? `No time · ${noTimeReason.trim() || 'reason required'}`
                              : 'Existing time/no-time choice'}
                        </p>
                        <p>
                          <span className="font-medium">Coverage:</span>{' '}
                          {finishCoverage.length
                            ? finishCoverage
                                .map(
                                  (row) => `${DOMAIN_LABEL[row.domain]} ${row.decision || 'required'} (${row.basis || 'reason required'})`,
                                )
                                .join('; ')
                            : 'Not applicable'}
                        </p>
                        <p>
                          <span className="font-medium">Next moves:</span>{' '}
                          {(f.close_next_moves ?? []).length
                            ? `${(f.close_next_moves ?? []).map((move) => `${move.text} (${move.state})`).join('; ')} — complete/clear; external waits stay as obligations`
                            : 'No active or proposed move'}
                        </p>
                        <p>
                          <span className="font-medium">Status:</span> {status || 'Closed status required'}
                        </p>
                        <div>
                          <p className="font-medium">
                            {customerCloseTickets.length === 0 ? 'Tickets affected:' : 'Customer tickets affected:'}
                          </p>
                          <ul className="list-disc pl-5">
                            {customerCloseTickets.map((ticket) => (
                              <li key={ticket.desk_ticket_id}>
                                #{ticket.ticket_number} · {ticket.status || 'status unavailable'} ·{' '}
                                {(ticketNotify[ticket.desk_ticket_id] ?? notifyCustomer) ? 'email customer' : 'silent'}
                              </li>
                            ))}
                            {[...requiredSignalTickets, ...checkedSignalTickets].map((ticket) => (
                              <li key={ticket.desk_ticket_id}>#{ticket.ticket_number} · alert · no customer email</li>
                            ))}
                          </ul>
                          <p className="mt-1 text-muted-foreground">Vendor, contractor and internal tickets are never included.</p>
                        </div>
                      </div>
                    </details>
                  </div>
                  {customerCloseTickets.length > 1 ? (
                    <details className="text-xs text-muted-foreground">
                      <summary className="cursor-pointer">Per-ticket overrides</summary>
                      <div className="mt-2 grid gap-1.5 sm:grid-cols-2">
                        {customerCloseTickets.map((ticket) => {
                          const checked = ticketNotify[ticket.desk_ticket_id] ?? notifyCustomer
                          return (
                            <label key={ticket.desk_ticket_id} className="flex items-center gap-2 text-sm text-foreground">
                              <Checkbox
                                checked={checked}
                                onCheckedChange={(value) =>
                                  setTicketNotify((current) => ({
                                    ...current,
                                    [ticket.desk_ticket_id]: Boolean(value),
                                  }))
                                }
                                aria-label={`Notify customer for ticket ${ticket.ticket_number}`}
                              />
                              #{ticket.ticket_number}: {checked ? 'email' : 'silent'}
                            </label>
                          )
                        })}
                      </div>
                    </details>
                  ) : null}
                </div>
                {optionalSignalTickets.length > 0 ? (
                  <fieldset className="space-y-1.5 rounded-md border border-border px-2.5 py-2">
                    <legend className="px-1 text-sm font-medium">Alert tickets</legend>
                    {optionalSignalTickets.map((ticket) => {
                      const checked = signalClose[ticket.desk_ticket_id] ?? ticket.close_by_default ?? true
                      return (
                        <label key={ticket.desk_ticket_id} className="flex items-start gap-2 text-sm">
                          <Checkbox
                            checked={checked}
                            onCheckedChange={(value) =>
                              setSignalClose((current) => ({
                                ...current,
                                [ticket.desk_ticket_id]: Boolean(value),
                              }))
                            }
                            aria-label={`Close alert ticket ${ticket.ticket_number}`}
                          />
                          <span>{alertCloseCopy([ticket])}</span>
                        </label>
                      )
                    })}
                  </fieldset>
                ) : null}
                {(f.contractor_threads?.length ?? 0) > 0 ? (
                  <fieldset className="space-y-1.5 rounded-md border border-border px-2.5 py-2">
                    <legend className="px-1 text-sm font-medium">Close open contractor threads too?</legend>
                    {(f.contractor_threads ?? []).map((thread) => {
                      const checked = contractorClose[thread.desk_ticket_id] ?? thread.close_by_default
                      const number = thread.ticket_number ? `#${thread.ticket_number}` : `#${thread.desk_ticket_id}`
                      return (
                        <label key={thread.desk_ticket_id} className="flex items-start gap-2 text-sm">
                          <Checkbox
                            checked={checked}
                            onCheckedChange={(value) =>
                              setContractorClose((current) => ({
                                ...current,
                                [thread.desk_ticket_id]: Boolean(value),
                              }))
                            }
                            aria-label={`Close ${thread.party_name} ${number}`}
                          />
                          <span>
                            <span className="font-medium">
                              {thread.party_name} {number}
                            </span>
                            {thread.obligation ? <span className="block text-warning">{thread.obligation}</span> : null}
                          </span>
                        </label>
                      )
                    })}
                  </fieldset>
                ) : null}
                <details>
                  <summary className="cursor-pointer text-sm text-muted-foreground">Optional private note</summary>
                  <div className="mt-2 space-y-2">
                    <NoteAuthorPicker
                      value={noteOwner}
                      onChange={setNoteOwner}
                      people={f.staff}
                      me={me.data}
                      canDelegate={me.data?.role === 'manager' || me.data?.role === 'cover'}
                    />
                    <NoteField
                      value={note}
                      picked={mentions}
                      placeholder="Optional. Type @ to mention someone."
                      onChange={(text, picked) => {
                        setNote(text)
                        setMentions(picked)
                      }}
                    />
                  </div>
                </details>
                {workflowGateApplies && workflow.data && remainingWorkflowGates.length ? (
                  <div className="rounded bg-warning-soft px-2 py-2 text-sm text-warning" data-testid="tech-workflow-close-gate">
                    <p className="font-medium">Close is disabled. Still needed:</p>
                    <ul className="list-disc pl-5">
                      {remainingWorkflowGates.map((gate) => (
                        <li key={gate.key}>{gate.label}</li>
                      ))}
                    </ul>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      className="mt-2"
                      onClick={() => {
                        onClose()
                        requestAnimationFrame(() =>
                          document.querySelector<HTMLElement>('[aria-label$=" workflow"]')?.scrollIntoView({ block: 'center' }),
                        )
                      }}
                    >
                      Finish guided workflow
                    </Button>
                  </div>
                ) : workflowGateApplies && !workflow.isPending && !workflow.data ? (
                  <p className="rounded bg-warning-soft px-2 py-2 text-sm text-warning">
                    Choose and complete a guided workflow before closing.
                  </p>
                ) : null}
              </Section>
            ) : null}

            {waitingCustomer ? (
              <Section title="Waiting on customer">
                <SuggestionChips
                  suggestions={nextSuggestions}
                  label={(suggestion) => suggestion.value.text}
                  selectedId={picked.next_move?.suggestion.id}
                  onPick={pickNext}
                  onIgnore={(suggestion) => ignore('next_move', suggestion)}
                  prominent
                  hotkeys
                />
                <label className="block space-y-1 text-sm font-medium">
                  What we asked
                  <Input
                    aria-label="What we asked"
                    value={nextMove.text}
                    onChange={(event) => {
                      const text = event.currentTarget.value
                      setNextMove((current) => ({ ...current, text }))
                    }}
                    placeholder="What the customer needs to answer or send"
                  />
                </label>
                <label className="block space-y-1 text-sm font-medium">
                  Follow-up date
                  <Input
                    type="datetime-local"
                    aria-label="Follow-up date"
                    value={nextMove.due}
                    onChange={(event) => {
                      const due = event.currentTarget.value
                      setNextMove((current) => ({ ...current, due }))
                    }}
                  />
                </label>
              </Section>
            ) : null}

            {scheduling ? (
              <>
                <Section title="Booking">
                  <p className="text-sm">
                    {f.booking_at ? `Booked for ${formatOfficeDateTime(f.booking_at)}` : 'No booking is attached yet.'}
                  </p>
                  <a href="/schedule?tab=pipeline" className="text-sm font-medium text-primary underline">
                    Open Schedule to review or book
                  </a>
                </Section>
                <Section title="Next move" hint={f.next_move ? `Now: ${f.next_move.text}` : 'editable before save'}>
                  <SuggestionChips
                    suggestions={nextSuggestions}
                    label={(suggestion) => suggestion.value.text}
                    selectedId={picked.next_move?.suggestion.id}
                    onPick={pickNext}
                    onIgnore={(suggestion) => ignore('next_move', suggestion)}
                    prominent
                    hotkeys
                  />
                  <NextMoveFields
                    value={nextMove}
                    onChange={setNextMove}
                    people={f.people ?? []}
                    staff={f.staff}
                    contractors={firms.isError ? null : firms.data}
                  />
                </Section>
              </>
            ) : null}

            {working ? (
              <>
                {wrapup ? (
                  <div className="flex justify-start">
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      aria-expanded={wrapupExtras}
                      onClick={() => setWrapupExtras((shown) => !shown)}
                    >
                      {wrapupExtras ? 'Hide time and next move' : 'Add time or change next move'}
                    </Button>
                  </div>
                ) : null}
                <div className={wrapup && !wrapupExtras ? 'hidden' : 'space-y-3'}>
                  <Section title="Time" hint={f.manual_minutes ? `${f.manual_minutes} min logged so far` : undefined}>
                    <TimeSection
                      value={time}
                      onChange={setTime}
                      inputRef={timeRef}
                      defaults={timeDefaults.data}
                      people={f.staff}
                      meId={me.data?.id}
                      canDelegate={me.data?.role === 'manager' || me.data?.role === 'cover'}
                    />
                    <SuggestionChips
                      suggestions={timeSuggestions}
                      label={(suggestion) => `${suggestion.value.minutes} min · ${DOMAIN_LABEL[suggestion.value.domain]}`}
                      selectedId={picked.time?.suggestion.id}
                      onPick={pickTime}
                      onIgnore={(suggestion) => ignore('time', suggestion)}
                    />
                  </Section>
                  <Section
                    title="Next move"
                    hint={f.next_move ? `Now: ${f.next_move.text}` : monitoring ? 'required while monitoring' : 'optional'}
                  >
                    <SuggestionChips
                      suggestions={nextSuggestions}
                      label={(suggestion) => suggestion.value.text}
                      selectedId={picked.next_move?.suggestion.id}
                      onPick={pickNext}
                      onIgnore={(suggestion) => ignore('next_move', suggestion)}
                      prominent
                      hotkeys
                    />
                    <NextMoveFields
                      value={nextMove}
                      onChange={setNextMove}
                      people={f.people ?? []}
                      staff={f.staff}
                      contractors={firms.isError ? null : firms.data}
                    />
                    {nmIncomplete ? <p className="text-xs text-warning">Pick an owner and a due time, or clear the text.</p> : null}
                  </Section>
                </div>
              </>
            ) : null}

            {!compactClose ? (
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1 rounded-md border border-border px-3 py-2 text-sm">
                <span className="min-w-0 flex-1 basis-48 break-words text-muted-foreground">
                  Owner {f.owner?.name ?? 'Unassigned'} · Priority {f.priority ?? 'None'}
                </span>
                <Button type="button" size="sm" variant="ghost" onClick={() => setMoreFields((value) => !value)}>
                  {moreFields ? 'Fewer fields' : 'More fields'}
                </Button>
                <Button type="button" size="sm" variant="ghost" onClick={() => setMoreFields(true)}>
                  Change
                </Button>
              </div>
            ) : null}

            {!compactClose && moreFields ? (
              <Section title="More fields" hint="everything remains reachable">
                <div className="space-y-1">
                  <p className="text-sm font-medium">Owner</p>
                  <OwnerSelect
                    value={owner}
                    onChange={setOwner}
                    people={staff.data?.people}
                    unreachable={staff.data?.desk_directory === 'unreachable'}
                  />
                  <SuggestionChips
                    suggestions={ownerSuggestions}
                    label={(suggestion) => suggestion.value.name}
                    selectedId={picked.owner?.suggestion.id}
                    onPick={pickOwner}
                    onIgnore={(suggestion) => ignore('owner', suggestion)}
                  />
                </div>
                {staff.isError ? <p className="text-xs text-warning">Staff could not be listed; the owner cannot be changed now.</p> : null}
                {!closingAction ? (
                  <div className="space-y-1">
                    <p className="text-sm font-medium">Private note</p>
                    <NoteAuthorPicker
                      value={noteOwner}
                      onChange={setNoteOwner}
                      people={f.staff}
                      me={me.data}
                      canDelegate={me.data?.role === 'manager' || me.data?.role === 'cover'}
                    />
                    <NoteField
                      value={note}
                      picked={mentions}
                      placeholder="Optional. Type @ to mention someone."
                      onChange={(text, picked) => {
                        setNote(text)
                        setMentions(picked)
                      }}
                    />
                  </div>
                ) : null}
                {!closingAction && !working ? (
                  <div className="space-y-1">
                    <p className="text-sm font-medium">Time</p>
                    <TimeSection
                      value={time}
                      onChange={setTime}
                      inputRef={timeRef}
                      defaults={timeDefaults.data}
                      people={f.staff}
                      meId={me.data?.id}
                      canDelegate={me.data?.role === 'manager' || me.data?.role === 'cover'}
                    />
                    <SuggestionChips
                      suggestions={timeSuggestions}
                      label={(suggestion) => `${suggestion.value.minutes} min · ${DOMAIN_LABEL[suggestion.value.domain]}`}
                      selectedId={picked.time?.suggestion.id}
                      onPick={pickTime}
                      onIgnore={(suggestion) => ignore('time', suggestion)}
                    />
                  </div>
                ) : null}
                {!closingAction && !working && !waitingCustomer && !scheduling ? (
                  <div className="space-y-1">
                    <p className="text-sm font-medium">Next move</p>
                    <SuggestionChips
                      suggestions={nextSuggestions}
                      label={(suggestion) => suggestion.value.text}
                      selectedId={picked.next_move?.suggestion.id}
                      onPick={pickNext}
                      onIgnore={(suggestion) => ignore('next_move', suggestion)}
                      prominent
                      hotkeys
                    />
                    <NextMoveFields
                      value={nextMove}
                      onChange={setNextMove}
                      people={f.people ?? []}
                      staff={f.staff}
                      contractors={firms.isError ? null : firms.data}
                    />
                  </div>
                ) : null}
                <div className="flex justify-end">
                  <CaseLinksPopover caseId={caseUuid} label="Links for the next step" />
                </div>
              </Section>
            ) : null}
          </fieldset>

          {error ? (
            <p role="alert" className="rounded bg-destructive-soft px-2 py-1.5 text-sm text-destructive">
              {error.message.replace(/[.\s]*$/, '.')}{' '}
              {error.nothingWritten ? 'Nothing was written.' : 'Save again will safely retry the same request.'}
            </p>
          ) : null}
          {result ? (
            <section className="space-y-2 rounded-lg border border-border p-3" aria-label="Update results">
              <ul className="space-y-1.5">
                {result.parts.map((p) => (
                  <PartRow key={p.part} p={p} />
                ))}
              </ul>
              {!result.ok ? (
                <p className="text-xs text-muted-foreground">
                  The green parts are saved. Retry sends only the rest; nothing is written twice.
                </p>
              ) : null}
            </section>
          ) : null}
          <div className="flex flex-wrap items-center justify-end gap-2">
            {saveBlockedReasons.length > 0 ? (
              <div className="w-full text-sm text-warning" data-testid="save-blocked-reasons">
                <p className="font-medium">Still needed:</p>
                <ul className="list-disc pl-5">
                  {saveBlockedReasons.map((reason) => (
                    <li key={reason}>{reason}</li>
                  ))}
                </ul>
              </div>
            ) : null}
            {incompleteCoverageDomains.map((d) => (
              <Button key={d} type="button" size="sm" variant="outline" onClick={() => focusCoverage(d)}>
                Choose {DOMAIN_LABEL[d]} case coverage
              </Button>
            ))}
            {contradictionBlocks && timeDomain && !incompleteCoverageDomains.includes(timeDomain) ? (
              <Button type="button" size="sm" variant="outline" onClick={() => focusCoverage(timeDomain)}>
                Set {DOMAIN_LABEL[timeDomain]} case coverage
              </Button>
            ) : null}
            {billableIncomplete ? (
              <Button type="button" size="sm" variant="outline" onClick={focusTimeBilling}>
                Choose whether this time is billed
              </Button>
            ) : null}
            {closingAction ? <span className="mr-auto text-sm text-muted-foreground">{notificationSummary}</span> : null}
            <DialogClose render={<Button type="button" variant="ghost" />}>{locked ? 'Close' : 'Cancel'}</DialogClose>
            {locked ? (
              retryable ? (
                <Button type="button" variant="primary" pending={busy} onClick={retry}>
                  <RefreshCw /> Retry failed parts
                </Button>
              ) : null
            ) : (
              <Button type="submit" variant="primary" disabled={!canSave && !busy} pending={busy}>
                {closingAction ? (compactClose ? 'Confirm close' : 'Save and close') : 'Save'}
              </Button>
            )}
          </div>
        </form>
      )}
    </Dialog>
  )
}
